/**
 * Data Validation Utilities for GeoLift and Power Calculator
 * Validates uploaded data files and detects appropriate column mappings
 */

import Papa from 'papaparse';

export type FieldType = 'location' | 'kpi' | 'date' | 'spend';

// Canonical column name each FieldType must be renamed to before it reaches
// the backend - matches what the GeoLift pipeline (and /validateData) expects.
const CANONICAL_COLUMN_NAME: Record<FieldType, string> = {
  date: 'date',
  location: 'location',
  kpi: 'Y',
  spend: 'adspend',
};

// -- /validateData response shape (mirrors GeoLiftDataValidationResponse in
// adrsta-models/app/controllers/geo_lift_resource.py::validateGeoLiftData) --
export interface ValidationErrorExample {
  rowIndex: number;
  value: string | null;
}

export interface GeoLiftValidationError {
  type: string;
  column?: string;
  columns?: string[];
  count?: number;
  examples?: ValidationErrorExample[];
  message: string;
}

export interface GeoLiftValidationWarning {
  type: string;
  count: number;
  examples: string[];
  message: string;
}

export interface GeoLiftDataValidationResponse {
  isValid: boolean;
  errors: GeoLiftValidationError[];
  warnings: GeoLiftValidationWarning[];
  summary: {
    totalRows?: number;
    uniqueLocations?: number;
    dateRange?: { min: string | null; max: string | null };
    locationClassification?: {
      state: number;
      state_abbrev: number;
      dma_code: number;
      city_state: number;
      unrecognized: number;
    };
    foundColumns?: string[];
  };
  preview: {
    columns: string[];
    sampleZones: {
      start?: Record<string, string | null>[];
      middle?: Record<string, string | null>[];
      end?: Record<string, string | null>[];
    };
  };
}

// Fields that the backend parses with pd.to_numeric, which chokes on
// currency formatting (pd.to_numeric("$1,234.56") -> NaN) even though the
// value is obviously numeric. Strip $ and thousands-separator commas here so
// a normally-formatted Excel export doesn't get flagged as invalid.
const NUMERIC_FIELDS: FieldType[] = ['kpi', 'spend'];

function normalizeNumericString(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '') return trimmed;
  // Preserve values that aren't currency-formatted numbers (e.g. already
  // blank/non-numeric) untouched - only strip $ and comma separators.
  return trimmed.replace(/[$,]/g, '');
}

/**
 * Rename a raw uploaded CSV's columns to the canonical names the backend
 * expects (date/location/Y/adspend), based on the user-confirmed column
 * mapping, and return a new File with that renamed content.
 *
 * This is the fix for the mapping selection previously being captured but
 * never applied - handleFileUpload used to upload the original file
 * untouched, so any file whose headers didn't already literally say
 * "date"/"location"/"Y" would fail backend processing regardless of what
 * the user mapped in ColumnMappingModal.
 */
export function applyColumnMapping(
  file: File,
  mappings: ColumnMapping[]
): Promise<File> {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        try {
          const activeMappings = mappings.filter((m) => !!m.detectedColumn);
          const renamedRows = result.data.map((row) => {
            const newRow: Record<string, string> = {};
            activeMappings.forEach((m) => {
              const sourceCol = m.detectedColumn as string;
              const rawValue = row[sourceCol] ?? '';
              newRow[CANONICAL_COLUMN_NAME[m.field]] = NUMERIC_FIELDS.includes(m.field)
                ? normalizeNumericString(rawValue)
                : rawValue;
            });
            return newRow;
          });

          const csvText = Papa.unparse(renamedRows);
          const renamedFile = new File([csvText], file.name, {
            type: 'text/csv',
          });
          resolve(renamedFile);
        } catch (err) {
          reject(err);
        }
      },
      error: (error) => reject(error),
    });
  });
}

export interface ColumnMapping {
  field: FieldType;
  label: string;
  detectedColumn: string | null;
  confidence: number;
  required: boolean;
  description: string;
}

export interface ValidationResult {
  isValid: boolean;
  needsMapping: boolean;
  mappings: ColumnMapping[];
  availableColumns: string[];
  message: string;
  errors: string[];
}

/**
 * Detect which column is likely the date column
 * Priority: Exact match > Partial match
 */
export function detectDateColumn(columns: string[]): { column: string | null; confidence: number } {
  // Priority 1: Exact matches (case-insensitive)
  const exactMatches = ['date', 'week', 'day', 'month', 'year', 'period', 'time', 'dt'];

  for (const col of columns) {
    const colLower = col.toLowerCase().trim();
    if (exactMatches.includes(colLower)) {
      return { column: col, confidence: 1.0 };
    }
  }

  // Priority 2: Partial matches
  const dateKeywords = ['date', 'week', 'day', 'month', 'year', 'period', 'time', 'dt', 'timestamp'];

  for (const col of columns) {
    const colLower = col.toLowerCase().trim();
    for (const keyword of dateKeywords) {
      if (colLower.includes(keyword)) {
        return { column: col, confidence: 0.8 };
      }
    }
  }

  return { column: null, confidence: 0 };
}

/**
 * Detect which column is likely the location column
 * Looks for location, state, city, zip, store, region, etc.
 */
export function detectLocationColumn(columns: string[]): { column: string | null; confidence: number } {
  // Priority 1: Exact matches
  const exactMatches = ['location', 'state', 'city', 'zip', 'store', 'region', 'area', 'market', 'dma', 'geo'];

  for (const col of columns) {
    const colLower = col.toLowerCase().trim();
    if (exactMatches.includes(colLower)) {
      return { column: col, confidence: 1.0 };
    }
  }

  // Priority 2: Partial matches
  const locationKeywords = ['location', 'state', 'city', 'zip', 'store', 'region', 'area', 'market', 'dma', 'geo', 'territory', 'place'];

  for (const col of columns) {
    const colLower = col.toLowerCase().trim();
    for (const keyword of locationKeywords) {
      if (colLower.includes(keyword)) {
        return { column: col, confidence: 0.8 };
      }
    }
  }

  return { column: null, confidence: 0 };
}

/**
 * Detect which column is likely the KPI column (sales/conversions)
 * Excludes already mapped columns
 */
export function detectKpiColumn(
  columns: string[],
  excludedColumns: string[]
): { column: string | null; confidence: number } {
  const availableColumns = columns.filter(col => !excludedColumns.includes(col));

  // Priority 1: Exact matches
  const exactMatches = ['sales', 'conversion', 'conversions', 'revenue', 'y', 'kpi', 'orders', 'transactions'];

  for (const col of availableColumns) {
    const colLower = col.toLowerCase().trim();
    if (exactMatches.includes(colLower)) {
      return { column: col, confidence: 1.0 };
    }
  }

  // Priority 2: Partial matches
  const kpiKeywords = ['sales', 'conversion', 'revenue', 'orders', 'transaction', 'purchase', 'kpi', 'target', 'outcome', 'volume', 'units'];

  for (const col of availableColumns) {
    const colLower = col.toLowerCase().trim();
    for (const keyword of kpiKeywords) {
      if (colLower.includes(keyword)) {
        return { column: col, confidence: 0.8 };
      }
    }
  }

  // Priority 3: If only one numeric-looking column left, assume it's KPI
  if (availableColumns.length === 1) {
    return { column: availableColumns[0], confidence: 0.5 };
  }

  return { column: null, confidence: 0 };
}

/**
 * Detect which column is likely the spend column (optional)
 * Excludes already mapped columns
 */
export function detectSpendColumn(
  columns: string[],
  excludedColumns: string[]
): { column: string | null; confidence: number } {
  const availableColumns = columns.filter(col => !excludedColumns.includes(col));

  // Priority 1: Exact matches
  const exactMatches = ['spend', 'cost', 'investment', 'budget', 'expense'];

  for (const col of availableColumns) {
    const colLower = col.toLowerCase().trim();
    if (exactMatches.includes(colLower)) {
      return { column: col, confidence: 1.0 };
    }
  }

  // Priority 2: Partial matches
  const spendKeywords = ['spend', 'cost', 'investment', 'budget', 'expense', 'expenditure', 'cpc', 'cpm', 'cpa'];

  for (const col of availableColumns) {
    const colLower = col.toLowerCase().trim();
    for (const keyword of spendKeywords) {
      if (colLower.includes(keyword)) {
        return { column: col, confidence: 0.8 };
      }
    }
  }

  return { column: null, confidence: 0 };
}

/**
 * Validate uploaded data and detect column mappings
 * Used for GeoLift and Power Calculator data uploads
 */
export function validateGeoLiftData(columns: string[]): ValidationResult {
  const mappings: ColumnMapping[] = [];
  const detectedMappings: { [key: string]: string } = {};
  const errors: string[] = [];

  // Validate that we have columns
  if (!columns || columns.length === 0) {
    return {
      isValid: false,
      needsMapping: true,
      mappings: [],
      availableColumns: [],
      message: 'No columns found in the uploaded file',
      errors: ['File appears to be empty or invalid']
    };
  }

  // Detect date column
  const dateResult = detectDateColumn(columns);
  mappings.push({
    field: 'date',
    label: 'Date / Time Period',
    detectedColumn: dateResult.column,
    confidence: dateResult.confidence,
    required: true,
    description: 'Date, week, or time period column (e.g., "Date", "Week", "Day")'
  });
  if (dateResult.column) {
    detectedMappings['date'] = dateResult.column;
  } else {
    errors.push('Could not detect date/time column. Please select the appropriate column.');
  }

  // Detect location column
  const locationResult = detectLocationColumn(columns);
  mappings.push({
    field: 'location',
    label: 'Location',
    detectedColumn: locationResult.column,
    confidence: locationResult.confidence,
    required: true,
    description: 'Geographic location identifier (e.g., "State", "City", "Store", "DMA")'
  });
  if (locationResult.column) {
    detectedMappings['location'] = locationResult.column;
  } else {
    errors.push('Could not detect location column. Please select the appropriate column.');
  }

  // Detect KPI column (exclude already mapped columns)
  const excludedForKpi = Object.values(detectedMappings);
  const kpiResult = detectKpiColumn(columns, excludedForKpi);
  mappings.push({
    field: 'kpi',
    label: 'KPI (Sales / Conversions)',
    detectedColumn: kpiResult.column,
    confidence: kpiResult.confidence,
    required: true,
    description: 'Key performance indicator (e.g., "Sales", "Conversions", "Revenue")'
  });
  if (kpiResult.column) {
    detectedMappings['kpi'] = kpiResult.column;
  } else {
    errors.push('Could not detect KPI column. Please select the appropriate column.');
  }

  // Detect spend column (optional, exclude already mapped columns)
  const excludedForSpend = Object.values(detectedMappings);
  const spendResult = detectSpendColumn(columns, excludedForSpend);
  mappings.push({
    field: 'spend',
    label: 'Spend (Optional)',
    detectedColumn: spendResult.column,
    confidence: spendResult.confidence,
    required: false,
    description: 'Marketing spend or cost data (optional)'
  });

  // Determine validation status
  const requiredMappings = mappings.filter(m => m.required);
  const missingRequired = requiredMappings.filter(m => !m.detectedColumn || m.confidence < 0.5);
  const lowConfidenceMappings = mappings.filter(m => m.detectedColumn && m.confidence < 0.8);

  const isValid = missingRequired.length === 0;
  const needsMapping = !isValid || lowConfidenceMappings.length > 0;

  let message = '';
  if (isValid && !needsMapping) {
    message = '✓ All required fields detected successfully!';
  } else if (missingRequired.length > 0) {
    const missing = missingRequired.map(m => m.label).join(', ');
    message = `Missing required fields: ${missing}. Please map the correct columns.`;
  } else {
    message = 'Some fields detected with low confidence. Please verify the column mappings.';
  }

  return {
    isValid,
    needsMapping,
    mappings,
    availableColumns: columns,
    message,
    errors
  };
}

/**
 * Convert frontend mappings to backend format
 * Example: { date: 'Week', location: 'State', kpi: 'Sales' }
 */
export function convertMappingsToBackendFormat(mappings: ColumnMapping[]) {
  const result: { [key: string]: string | null } = {};

  mappings.forEach(mapping => {
    result[mapping.field] = mapping.detectedColumn;
  });

  return result;
}

/**
 * Validate if required mappings are provided
 */
export function validateRequiredMappings(mappings: ColumnMapping[]): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  const requiredMappings = mappings.filter(m => m.required);

  requiredMappings.forEach(mapping => {
    if (!mapping.detectedColumn) {
      errors.push(`${mapping.label} is required but not mapped`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
}
